import { SubscriptionPlans } from '../../../../../../user/user/user.model';
import { MapModalData } from '../../../../../../user/map-redirect-modal/map-redirect-modal.component';
import { ROUTER_NAVIGATE } from '../../../../../../user/map-redirect-modal/map-redirect-modal-text';
// Type-only: keeps this leaf util free of a runtime edge into the access
// service, which pulls in the modal service and its dialog components.
import type {
  FeatureType,
  UserAccessService,
} from '../../../../../../user/access/user-access.service';

/**
 * True when a guest is looking at an indicator that signing up would NOT
 * unlock: it is behind the paywall, not merely behind an account.
 *
 * The guest upsell below promises a free trial, and for most locked
 * indicators that promise is true - the trial list is much longer than the
 * guest one. But a good part of the map is subscription-only, and offering
 * those a free trial sends the visitor through sign-up to the very same
 * padlock. Those get the plans modal instead, the one the popup's
 * "Know America Like It's Your Own" CTA opens.
 *
 * A missing feature constant (the comparison table renders tooltips without
 * one) can't be classified, so it keeps the old prompt.
 */
export function signUpWouldNotUnlock(
  feature: FeatureType | undefined,
  accessService: UserAccessService
): boolean {
  return (
    !!feature &&
    accessService.isUnauthorized &&
    accessService.isBySubscription(feature)
  );
}

export function generateInaccessibleModalData(
  featureDisplayName: string,
  isUnauthorized: boolean
): MapModalData {
  // For unauthorized users
  if (isUnauthorized) {
    return {
      headerText: `Unlock This with a Free Trial`,
      mainText: `You're using a guest version with limited access.`,
      additionalText:
        'Start your free trial to explore it now — no credit card needed.',
      buttonText: 'Sign Up For Free',
      URLtoGo: '/sign-up',
      actionType: ROUTER_NAVIGATE,
      hasBackdrop: true,
      gaEventOnButtonClick: 'start_free_trial_click',
    };
  }

  // Every subscription unlocks every indicator, so the only thing left to say
  // is what the cheapest one costs.
  const userFacingPlans: SubscriptionPlans[] = [
    SubscriptionPlans.HOME_SEARCH,
    SubscriptionPlans.REAL_ESTATE,
    SubscriptionPlans.SITE_SELECTION,
  ];

  // Read off the plans themselves rather than written down here: the number
  // was hardcoded and went stale the moment subscriptions were re-priced.
  //
  // Strips everything but digits and the decimal point, so a thousands
  // separator ('$2,499.95', which ENTERPRISE already carries) sorts as 2499
  // and not as 2 the way a bare '$' strip would.
  const amountOf = (price: string): number =>
    parseFloat(price.replace(/[^\d.]/g, ''));

  const lowestPrice = userFacingPlans
    .map((planKey) => SubscriptionPlans.description[planKey].priceMonthly)
    .filter((price): price is string => !!price)
    .sort((a, b) => amountOf(a) - amountOf(b))[0];

  return {
    headerText: `Unlock ${featureDisplayName}`,
    buttonText: 'Get started',
    URLtoGo: '/user/subscription',
    actionType: ROUTER_NAVIGATE,
    hasBackdrop: true,
    gaEventOnButtonClick: 'get_started_locked_param',
    mainText: `${featureDisplayName} is available with any subscription.`,
    additionalText: lowestPrice
      ? `Get full access to all map data starting ${lowestPrice}/month.`
      : `Get full access to all map data with any subscription.`,
  };
}
